'use client';

/**
 * Shared role-in-household form fields used by both add-member dialogs:
 *   - HouseholdMembersTab.tsx (household-side: add existing member)
 *   - MemberHouseholdsTab.tsx AddToHouseholdDialog (member-side: add to existing
 *     or create new household)
 *
 * Both dialogs solve different starting points (which side initiates the link)
 * but capture the same three membership attributes — role, relationship to
 * head, and displacement status. Keeping the field definitions, labels, and
 * defaults here ensures the two surfaces stay in sync.
 */

import React from 'react';

export const HOUSEHOLD_ROLE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'HEAD_OF_HOUSEHOLD', label: 'Head of Household' },
  { value: 'SPOUSE', label: 'Spouse' },
  { value: 'CHILD', label: 'Child' },
  { value: 'DEPENDENT', label: 'Dependent' },
  { value: 'OTHER', label: 'Other' },
];

export const HOUSEHOLD_ROLE_LABELS: Record<string, string> = Object.fromEntries(
  HOUSEHOLD_ROLE_OPTIONS.map((o) => [o.value, o.label])
);

interface MembershipRoleFieldsProps {
  role: string;
  onRoleChange: (role: string) => void;
  relationshipToHead: string;
  onRelationshipChange: (relationship: string) => void;
  isDisplaced: boolean;
  onDisplacedChange: (displaced: boolean) => void;
}

export function MembershipRoleFields({
  role,
  onRoleChange,
  relationshipToHead,
  onRelationshipChange,
  isDisplaced,
  onDisplacedChange,
}: MembershipRoleFieldsProps) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Role in Household</label>
          <select
            value={role}
            onChange={(e) => onRoleChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            {HOUSEHOLD_ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Relationship to Head</label>
          <input
            type="text"
            value={relationshipToHead}
            onChange={(e) => onRelationshipChange(e.target.value)}
            placeholder="e.g. Daughter, Son"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={isDisplaced}
          onChange={(e) => onDisplacedChange(e.target.checked)}
          className="rounded"
        />
        <span>Currently displaced</span>
      </label>
    </div>
  );
}
